import React from 'react';

import { PAGES, UNAUTHORIZED_PAGES } from 'common/enums';

import { Car } from 'components/icons-new/car';
import { DashboardIcon } from 'components/icons-new/dashboard';
import { HomeIcon } from 'components/icons-new/home';
import { DocumentApprovedIcon } from 'components/icons-new/document-approved';
import { BillIcon } from 'components/icons-new/bill';
import { ShoppingCart } from 'components/icons-new/shopping-cart';
import { UsedCar } from 'components/icons-new/used-car';
import { ArchiveIcon } from 'components/icons-new/archive';
import { SettingsIcon } from 'components/icons-new/settings';
import { ContractIcon } from 'components/icons-new/contract';
import { CustomersIcon } from 'components/icons-new/customers';
import { EmailIcon } from 'components/icons-new/email';
import { CarRepairIcon } from 'components/icons-new/car-repair';
import { DamagedCarIcon } from 'components/icons-new/damaged-car';
import { DriversLicenseIcon } from 'components/icons-new/drivers-license';
import { GasStationIcon } from 'components/icons-new/gas-station';
import { FinishIcon } from 'components/icons-new/finish';
import { FuelCardIcon } from 'components/icons-new/fuel-card';
import { CarCheckedIcon } from 'components/icons-new/car-checked';
import { CalendarCheckedIcon } from 'components/icons-new/calendar-checked';
import { CalendarPlanningIcon } from 'components/icons-new/calendar-planning';
import { WalletIcon } from 'components/icons-new/wallet';
import { CalculatorIcon } from 'components/icons-new/calculator';
import { NewCarIcon } from 'components/icons-new/new-car';
import { CarPriceIcon } from 'components/icons-new/car-price';
import { EnterpriceIcon } from 'components/icons-new/enterprice';
import { FinnIcon } from 'components/icons-new/finn';
import { CompanyIcon } from 'components/icons-new/company';
import { DriverLevelIcon } from 'components/icons-new/driver-level';
import { TransmissionIcon } from 'components/icons-new/transmission';
import { GearIcon } from 'components/icons-new/gear';
import { FileUploadIcon } from 'components/icons-new/file-upload';
import { UserIcon } from 'components/icons-new/user';
import { InfoHintIcon } from 'components/icons-new/info-hint';
import { NewDriverIcon } from 'components/icons-new/new-driver';
import { DocumentIcon } from 'components/icons-new/document';
import TopDealsIcon from 'components/icons-new/top-deals';
import { CheckedIcon } from 'components/icons-new/checked-icon';
import { CarWithGearIcon } from 'components/icons-new/car-with-gear';
import { EuroSignWithBorderIcon } from 'components/icons-new/euro-sign-with-border';
import { CarWithBorderIcon } from 'components/icons-new/car-with-border';
import { ReportIcon } from 'components/icons-new/report';
import { GreenEuroIcon } from 'components/icons-new/green-euro';
import { SupplierIcon } from 'components/icons-new/supplier';
import { WallEBussinesPartnerIcon } from 'components/icons-new/wall-e-bussines-partner';
import { DownloadsAndFaqIcon } from 'components/icons-new/downloads-and-faq-icon';
import { FileDownloadIcon } from 'components/icons-new/file-download';
import { EuroWithPercentageIcon } from 'components/icons-new/euro-with-percentage';

import { USER_ROLES } from '../../../constants';
import type { MainMenuItem } from '../side-menu-interfaces';
import { MainMenuTitles, SubMainMenuTitles } from '../side-menu-enums';
import { TireIcon } from 'components/icons-new/tire';

type CreateBaseMenuOptions = {
  damageLinks: string[];
  isCreateDamagePageAvailable: boolean;
  isDriver: boolean;
  isAdmin: boolean;
  reportDamageCustomLink: string;
};

export function createBaseMenu({
  damageLinks,
  isCreateDamagePageAvailable,
  isDriver,
  isAdmin,
  reportDamageCustomLink
}: CreateBaseMenuOptions): MainMenuItem[] {
  return [
    {
      menuTitle: MainMenuTitles.Dashboard,
      links: [PAGES.DASHBOARD],

      mainMenuImg: <DashboardIcon />,
      roles: [],
      subMenuItems: []
    },
    {
      menuTitle: MainMenuTitles.Report,
      links: [PAGES.REPORT],

      mainMenuImg: <ReportIcon />,
      roles: [],
      subMenuItems: []
    },
    {
      menuTitle: MainMenuTitles.Vehicle,
      mainMenuImg: <Car />,
      subMenuItems: [
        {
          links: [PAGES.REPAIRED],
          title: SubMainMenuTitles.RepairStatus,
          roles: [],
          subMenuImg: <CarRepairIcon />
        },
        ...(isDriver || isAdmin
          ? [
              {
                links: [reportDamageCustomLink],
                title: SubMainMenuTitles.ReportDamage,
                external: true,
                roles: [],
                subMenuImg: <DamagedCarIcon />
              }
            ]
          : []),
        {
          links: [PAGES.CREATE_DAMAGE],
          title: SubMainMenuTitles.CreateDamage,
          external: false,
          roles: [],
          availableForRoles: isCreateDamagePageAvailable ? [USER_ROLES.administrator.id, USER_ROLES.driver.id] : [],
          subMenuImg: <DamagedCarIcon />
        }
      ]
    },
    {
      menuTitle: MainMenuTitles.Fleet,

      mainMenuImg: <HomeIcon />,
      subMenuItems: [
        {
          links: [PAGES.MY_DRIVERS, PAGES.MY_DRIVERS_CREATE, PAGES.MY_DRIVERS_DETAILS],
          title: SubMainMenuTitles.Driver,
          roles: [],
          subMenuImg: <CustomersIcon />
        },
        {
          links: [PAGES.DRIVERS_CHECK],
          title: SubMainMenuTitles.DriverLicenseCheck,
          roles: [],
          subMenuImg: <DriversLicenseIcon />
        },
        {
          links: [PAGES.MY_VEHICLES, PAGES.MY_VEHICLES_DETAILS, PAGES.MY_VEHICLES_NEW_VEHICLE],
          title: SubMainMenuTitles.Vehicles,
          roles: [],
          subMenuImg: <Car />
        },
        {
          links: damageLinks,
          title: SubMainMenuTitles.Damage,
          roles: [],
          subMenuImg: <DamagedCarIcon />
        },
        {
          links: [PAGES.FUEL_REPORT],
          title: SubMainMenuTitles.FuelReport,
          roles: [],
          subMenuImg: <GasStationIcon />
        },
        {
          links: [
            PAGES.FUEL_CARDS,
            PAGES.FUEL_CARDS_ORDER,
            PAGES.FUEL_CARDS_ORDER_FUEL_CARD,
            PAGES.FUEL_CARDS_CREATE_FUEL_CARD
          ],
          title: SubMainMenuTitles.FuelCards,
          roles: [],
          subMenuImg: <FuelCardIcon />
        },
        {
          links: [PAGES.WHEELS_AND_TIRES],
          title: SubMainMenuTitles.WheelsAndTires,
          roles: [],
          subMenuImg: <TireIcon />
        },
        {
          links: [
            PAGES.ASSIGNMENTS,
            PAGES.ASSIGNMENTS_VEHICLE_TRANSFER,
            PAGES.ASSIGNMENTS_VEHICLE_TRANSFER_RECORD,
            PAGES.ASSIGNMENTS_ORDER_RENTAL_CAR,
            PAGES.ASSIGNMENTS_ACCIDENT_REPAIR,
            PAGES.ASSIGNMENTS_ACCIDENT_REPAIR_RECORD,
            PAGES.ASSIGNMENTS_LEASE_RETURN,
            PAGES.ASSIGNMENTS_LEASE_RETURN_RECORD,
            PAGES.ASSIGNMENTS_FUEL_RECEIPT_SUBMIT,
            PAGES.ASSIGNMENTS_ELECTRIC_CAR_CHARGING_COST
          ],
          title: SubMainMenuTitles.Assignments,
          roles: [],
          subMenuImg: <DocumentIcon />
        },
        {
          links: [PAGES.POOL_CALENDAR, PAGES.POOL_CALENDAR_NEW, PAGES.POOL_CALENDAR_VEHICLE],
          title: SubMainMenuTitles.PoolCarBookings,
          roles: [],
          subMenuImg: <CarCheckedIcon />
        },
        {
          links: [PAGES.MY_POOL_CALENDAR, PAGES.MY_POOL_CALENDAR_NEW, PAGES.MY_POOL_CALENDAR_VEHICLE],
          title: SubMainMenuTitles.MyPoolCarBookings,
          roles: [],
          subMenuImg: <CarCheckedIcon />
        },

        // {
        //   links: [PAGES.LONG_TERM_RENTAL],
        //   title: SubMainMenuTitles.LongTermRental,
        //   roles: [],
        //   subMenuImg: <Car />
        // },
        {
          links: [PAGES.LEASE_END],
          title: SubMainMenuTitles.LeaseEnd,
          roles: [],
          subMenuImg: <FinishIcon />
        }
      ]
    },
    {
      menuTitle: MainMenuTitles.Tasks,

      mainMenuImg: <DocumentApprovedIcon />,
      subMenuItems: [
        {
          links: [PAGES.TASKS],
          title: SubMainMenuTitles.Tasks,
          roles: [],
          subMenuImg: <CalendarCheckedIcon />
        }
      ]
    },
    {
      menuTitle: MainMenuTitles.Accounting,

      mainMenuImg: <BillIcon />,
      subMenuItems: [
        {
          links: [PAGES.VEHICLE_COSTS],
          title: SubMainMenuTitles.VehicleCost,
          roles: [],
          subMenuImg: <WalletIcon />
        },
        {
          links: [PAGES.MONTHLY_BILLS],
          title: SubMainMenuTitles.MonthlyBills,
          roles: [],
          subMenuImg: <BillIcon />
        },
        // {
        //   links: ['/eingangsrechnung/'],
        //   title: 'Eingangsrechnung',
        //   infoChip: 'beta',
        //   roles: [],
        //   subMenuImg: <BillIcon />
        // },
        {
          links: [PAGES.BILLING_ELECTRIC_CAR_CHARGING_COSTS],
          title: SubMainMenuTitles.WallBoxBilling,
          roles: [],
          subMenuImg: <CalculatorIcon />
        },
        {
          links: [PAGES.TANK_RECEIPT_SUBMIT],
          title: SubMainMenuTitles.SubmitFuelReceipt,
          roles: [],
          subMenuImg: <GasStationIcon />
        },
        {
          links: [
            PAGES.EINGANGSRECHNUNG,
            PAGES.EINGANGSRECHNUNG_EDIT,
            PAGES.EINGANGSRECHNUNG_CREATE,
            PAGES.EINGANGSRECHNUNG_UPLOAD
          ],
          title: SubMainMenuTitles.IncomingInvoices,
          indicator: true,
          indicatorText: 'Diese Eingangsrechnungen warten auf Deine Überprüfung.',
          roles: [],
          subMenuImg: <BillIcon />
        },
        {
          links: [PAGES.DOCUMENTMANAGER, PAGES.DOCUMENTMANAGER_UPLOAD, PAGES.DOCUMENTMANAGER_EDIT],
          title: SubMainMenuTitles.DocumentManager,
          indicator: true,
          indicatorText: 'Diese Dokumente sind noch nicht sortiert worden.',
          roles: [],
          subMenuImg: <DocumentIcon />
        },
        {
          links: [PAGES.FINES, PAGES.FINE_UPLOADING, PAGES.FINE_DETAILS],
          title: SubMainMenuTitles.Fines,
          indicator: true,
          indicatorText: 'Diese Bußgelder sind noch nicht sortiert worden.',
          roles: [],
          subMenuImg: <ContractIcon />
        },
        {
          links: [PAGES.CASH_ADVANCE],
          title: SubMainMenuTitles.CashAdvance,
          indicator: true,
          roles: [],
          subMenuImg: <CalculatorIcon />
        }
      ]
    },
    {
      menuTitle: isDriver ? MainMenuTitles.Order : MainMenuTitles.Orders,

      mainMenuImg: <ShoppingCart />,
      subMenuItems: [
        {
          links: [PAGES.CONFIGURATIONS, PAGES.NEW_CONFIGURATIONS, PAGES.CONFIGURATION_DETAILS],
          title: SubMainMenuTitles.Configurations,
          roles: [],
          subMenuImg: <NewCarIcon />
        },
        {
          links: [
            PAGES.EMPLOYEE_CONFIGURATIONS,
            PAGES.NEW_REFERENCE_CONFIGURATIONS,
            PAGES.EMPLOYEE_CONFIGURATION_DETAILS,
            PAGES.REFERENCE_VEHICLES_DETAILS
          ],
          title: SubMainMenuTitles.EmployeeConfigurations,
          roles: [],
          subMenuImg: <CarPriceIcon />
        },
        {
          links: [
            PAGES.SETUP_FRAMEWORK_AGREEMENT,
            PAGES.SETUP_LEASING_SETTINGS,
            PAGES.SETUP_FUEL_SURCHARGE,
            PAGES.SETUP_MOBILITY_BUDGET,
            PAGES.SETUP_CAR_POLICY,
            PAGES.SETUP_PARTNER
          ],
          title: SubMainMenuTitles.CockpitSetup,
          roles: [],
          subMenuImg: <SettingsIcon />
        },
        {
          links: [PAGES.ORDERS, PAGES.ORDER_DETAILS],
          title: SubMainMenuTitles.Orders,
          roles: [],
          indicator: true,
          indicatorText: 'Diese Bestellungen warten auf Deine Überprüfung.',
          underline: true,
          subMenuImg: <ShoppingCart />
        },
        {
          links: [PAGES.COCKPIT],
          title: SubMainMenuTitles.Cockpit,
          roles: [],
          subMenuImg: <CarWithGearIcon />
        },
        {
          links: [PAGES.APPROVALS, PAGES.APPROVALS_DETAILS],
          title: SubMainMenuTitles.Approvals,
          roles: [],
          subMenuImg: <CheckedIcon />
        }
      ]
    },
    {
      menuTitle: MainMenuTitles.Requests,
      mainMenuImg: <ArchiveIcon />,
      subMenuItems: [
        {
          links: [PAGES.REQUESTS, PAGES.REQUESTS_DETAILS],
          title: SubMainMenuTitles.VehicleRequests,
          indicator: true,
          indicatorText: 'Diese Fahrzeuganfragen warten auf Deine Überprüfung.',
          roles: [],
          subMenuImg: <NewCarIcon />
        },
        {
          links: [PAGES.LEASING_REQUESTS, PAGES.LEASING_REQUESTS_DETAILS],
          title: SubMainMenuTitles.LeasingRequests,
          roles: [],
          subMenuImg: <CarPriceIcon />
        },
        {
          links: [PAGES.MASS_DATA_CALCULATION, PAGES.MASS_DATA_CALCULATION_DETAILS],
          title: SubMainMenuTitles.MassDataCalculation,
          roles: [],
          subMenuImg: <CarPriceIcon />
        }
      ]
    },
    {
      menuTitle: MainMenuTitles.MyRequests,
      mainMenuImg: <ArchiveIcon />,
      subMenuItems: [
        {
          links: [PAGES.MY_REQUESTS],
          title: SubMainMenuTitles.MyRequests,
          roles: [],
          subMenuImg: <ArchiveIcon />
        }
      ]
    },
    {
      menuTitle: MainMenuTitles.LeasingContracts,

      mainMenuImg: <ContractIcon />,
      subMenuItems: [
        {
          links: [PAGES.LEASING_CONTRACTS, PAGES.LEASING_CONTRACT_DETAILS],
          title: SubMainMenuTitles.LeasingContracts,
          indicator: true,
          indicatorText: 'Diese Leasingverträge warten auf Deine Überprüfung.',
          roles: [],
          subMenuImg: <DocumentIcon />
        }
      ]
    },
    {
      menuTitle: MainMenuTitles.Settings,

      mainMenuImg: <SettingsIcon />,
      subMenuItems: [
        {
          links: [
            PAGES.COMPANIES_BUSINESS_DETAILS,
            PAGES.COMPANIES_CONTACT,
            PAGES.COMPANIES_COMPANY,
            PAGES.COMPANIES_ORDER_CONTROL
          ],
          title: SubMainMenuTitles.Companies,
          roles: [],
          subMenuImg: <CompanyIcon />
        },
        {
          links: [
            PAGES.FLEET_SETTINGS_NOTIFICATION,
            PAGES.FLEET_SETTINGS_POOL_CALENDAR,
            PAGES.FLEET_SETTINGS_CONSUMPTION_SURCHARGE,
            PAGES.FLEET_SETTINGS_INSPECTION_DATES
          ],
          title: SubMainMenuTitles.FleetSettings,
          roles: [],
          subMenuImg: <CarWithBorderIcon />
        },
        {
          links: [
            PAGES.ACCOUNTING_SETTINGS_PAYMENT,
            PAGES.ACCOUNTING_SETTINGS_LEDGER_ACCOUNT_MAPPING,
            PAGES.ACCOUNTING_SETTINGS_COST_CENTERS
          ],
          title: SubMainMenuTitles.AccountingSettings,
          roles: [],
          underline: true,
          subMenuImg: <EuroSignWithBorderIcon />
        },
        {
          links: [PAGES.SUPPLIERS, PAGES.SUPPLIER],
          title: SubMainMenuTitles.Suppliers,
          roles: [],
          subMenuImg: <SupplierIcon />
        },
        {
          links: [PAGES.REFERENCE_VEHICLES],
          title: SubMainMenuTitles.ReferenceVehicle,
          roles: [],
          underline: true,
          subMenuImg: <CarCheckedIcon />
        },
        {
          links: [PAGES.DOCUMENTS, PAGES.DOCUMENTS_CONTRACT],
          title: SubMainMenuTitles.Documents,
          roles: [],
          subMenuImg: <ContractIcon />
        },
        {
          links: [PAGES.CONTRACTS],
          title: SubMainMenuTitles.Contracts,
          roles: [],
          underline: true,
          subMenuImg: <DocumentIcon />
        },
        {
          links: [PAGES.ADMIN_CENTER],
          title: SubMainMenuTitles.AdminCenter,
          roles: [],
          subMenuImg: <CustomersIcon />
        },
        {
          links: [PAGES.SETUP_ASSISTANT],
          title: SubMainMenuTitles.SetupAssistant,
          roles: [],
          subMenuImg: <GearIcon />
        },
        {
          links: [PAGES.IMPORT],
          title: SubMainMenuTitles.Import,
          roles: [],
          subMenuImg: <FileUploadIcon />
        },
        {
          links: [PAGES.PERSONAL_EMAIL_TEMPLATE],
          title: SubMainMenuTitles.PersonalEmailTemplate,
          roles: [],
          subMenuImg: <EmailIcon />
        },

        {
          links: [PAGES.ACCOUNT_SETTINGS],
          hideIfParams: true,
          title: SubMainMenuTitles.UserSettings,
          roles: [],
          subMenuImg: <UserIcon />
        }
      ]
    },
    {
      menuTitle: MainMenuTitles.MarketSquare,
      mainMenuImg: <DocumentApprovedIcon />,
      roles: [],
      subMenuItems: [
        {
          links: [UNAUTHORIZED_PAGES.ENTERPRISE],
          external: true,
          title: SubMainMenuTitles.Enterprise,
          roles: [],
          subMenuImg: <EnterpriceIcon />
        },
        {
          links: [PAGES.BOOKING_RENTAL_CAR],
          title: SubMainMenuTitles.Enterprise,
          roles: [],
          subMenuImg: <EnterpriceIcon />
        },
        {
          links: [UNAUTHORIZED_PAGES.FINN],
          external: true,
          title: SubMainMenuTitles.Finn,
          roles: [],
          subMenuImg: <FinnIcon />
        },
        {
          title: SubMainMenuTitles.GREENHOUSE_GAS_EMISSION_QUOTA,
          links: [PAGES.GREENHOUSE_GAS_EMISSION_QUOTA],
          subMenuImg: <GreenEuroIcon />,
          roles: []
        },
        {
          title: SubMainMenuTitles.WALL_E_BUSINESS_PARTNER_PROMOTION,
          links: [PAGES.WALL_E_BUSINESS_PARTNER_PROMOTION],
          subMenuImg: <WallEBussinesPartnerIcon />,
          roles: []
        },
        {
          links: [PAGES.TOP_DEALS],
          title: SubMainMenuTitles.TopDeals,
          roles: [],
          subMenuImg: <TopDealsIcon />
        }
      ]
    },
    {
      menuTitle: MainMenuTitles.BecomeCustomer,

      mainMenuImg: <ContractIcon />,
      subMenuItems: [
        {
          links: [PAGES.PACKAGES, PAGES.PACKAGE_PAGE, PAGES.ADD_COMPANY],
          title: SubMainMenuTitles.PackagesAndPrice,
          roles: [],
          subMenuImg: <ShoppingCart />,
          showWhenSiteUnavailable: true
        },
        {
          links: [PAGES.DECISION_SUPPORT],
          title: SubMainMenuTitles.DecisionSupport,
          roles: [],
          subMenuImg: <InfoHintIcon />,
          showWhenSiteUnavailable: true
        },
        {
          links: [PAGES.CLIENTS_FLEETMANAGER, PAGES.INVITE_CLIENT_FLEETMANAGER],
          title: SubMainMenuTitles.CustomersRecruitCustomers,
          roles: [],
          subMenuImg: <NewDriverIcon />,
          showWhenSiteUnavailable: true
        },
        {
          links: [PAGES.CREATE_OFFER, PAGES.CREATE_OFFER_PROCESS],
          title: SubMainMenuTitles.CreateOffer,
          roles: [],
          subMenuImg: <BillIcon />,
          showWhenSiteUnavailable: true
        }
      ]
    },
    {
      menuTitle: MainMenuTitles.Customers,

      mainMenuImg: <CustomersIcon />,
      subMenuItems: [
        {
          links: [PAGES.CLIENTS_DEALER],
          title: SubMainMenuTitles.Customers,
          roles: [],
          subMenuImg: <CustomersIcon />
        },
        {
          links: [PAGES.INVITE_CLIENT_DEALER, PAGES.INVITE_CLIENT_DEALER],
          title: SubMainMenuTitles.InviteCustomers,
          roles: [],
          subMenuImg: <NewDriverIcon />
        },
        {
          links: [PAGES.DISCOUNTS],
          title: SubMainMenuTitles.Discounts,
          roles: [],
          subMenuImg: <EuroWithPercentageIcon />
        }
      ]
    },
    {
      menuTitle: MainMenuTitles.DownloadsAndFaq,
      links: [PAGES.FAQ_AND_DOCUMENTS],
      mainMenuImg: <DownloadsAndFaqIcon />,
      indicator: true,
      subMenuItems: [
        {
          links: [PAGES.EXPORTS],
          title: SubMainMenuTitles.Exports,
          roles: [],
          indicator: true,
          subMenuImg: <FileDownloadIcon />
        },
        {
          links: [PAGES.SERVICE_PROVIDER_TASKS],
          title: SubMainMenuTitles.ServiceProvider,
          indicator: true,
          roles: [],
          subMenuImg: <CalendarPlanningIcon />
        },
        {
          links: [PAGES.FAQ_AND_DOCUMENTS],
          title: SubMainMenuTitles.DownloadsAndFaq,
          roles: [],
          subMenuImg: <DownloadsAndFaqIcon />
        },
        {
          title: SubMainMenuTitles.Contact,
          links: [PAGES.SUPPORT],
          subMenuImg: <EmailIcon />,
          roles: []
        }
      ]
    },
    {
      menuTitle: MainMenuTitles.APIDoc,
      links: [PAGES.SWAGGER],
      mainMenuImg: <DocumentApprovedIcon />,
      roles: [],
      subMenuItems: []
    }
  ];
}
